import api from './api';

export interface Transaction {
  id: string;
  amount: number;
  description: string;
  type: 'expense' | 'income' | 'transfer';
  category?: string;
  subcategory?: string;
  currency: string;
  date: string;
  time?: string;
  merchantName?: string;
  location?: string;
  notes?: string;
  receiptUrl?: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  inputMethod: 'manual' | 'telegram' | 'voice' | 'ocr' | 'api';
  metadata?: Record<string, any>;
  originalText?: string;
  isRecurring: boolean;
  recurringPattern?: string;
  exchangeRate?: number;
  originalAmount?: number;
  originalCurrency?: string;
  userId: string;
  /** Who recorded it. Only returned when viewing a shared context. */
  user?: TransactionAuthor;
  /** The shared context it belongs to; the personal context otherwise. */
  contextId?: string | null;
  /** Who paid, when not the author. Null means the author did. */
  paidByUserId?: string | null;
  /** The payer's name, when someone else paid. */
  paidBy?: TransactionAuthor | null;
  /** Who owes what part of it. Empty means the whole amount is the payer's own. */
  shares?: TransactionShareView[];
  /** What it costs the signed-in user; only in the personal view. */
  personalAmount?: number;
  /** Settlements recorded together with this expense ("she already paid me back"). */
  linkedSettlements?: LinkedSettlement[];
  createdAt: string;
  updatedAt: string;
}

export interface TransactionAuthor {
  id: string;
  firstName: string;
  lastName: string;
}

export interface TransactionShareView {
  id?: string;
  userId: string;
  amount: number;
  user?: TransactionAuthor;
}

export interface LinkedSettlement {
  id: string;
  /** Who paid the payer back. */
  fromUserId: string;
  amount: number;
}

/**
 * What the other participants already paid back, recorded with the expense.
 * `full`: everyone but the payer paid their part; `partial`: the listed
 * amounts; `none`: nobody yet (on an edit: forget the ones recorded with it).
 */
export interface SettledRequest {
  mode: 'none' | 'full' | 'partial';
  paid?: Array<{ userId: string; amount: number }>;
}

/** How to divide an expense among the members of its context. */
export interface SplitRequest {
  mode: 'equal' | 'custom' | 'none' | 'weighted';
  participantIds?: string[];
  shares?: Array<{ userId: string; amount: number }>;
  /** For `weighted`: a weight per member (the context's fixed proportions). */
  weights?: Record<string, number>;
  settled?: SettledRequest;
}

export interface CreateTransactionData {
  amount: number;
  description: string;
  type: 'expense' | 'income' | 'transfer';
  category?: string;
  subcategory?: string;
  dashboardCategory?: string;
  /** Shared context the transaction belongs to; omitted for personal ones. */
  contextId?: string;
  /** Who fronted the money, when not the signed-in user; only in a shared context. */
  paidByUserId?: string;
  currency?: string;
  date?: string;
  time?: string;
  merchantName?: string;
  location?: string;
  notes?: string;
  receiptUrl?: string;
  inputMethod?: 'manual' | 'telegram' | 'voice' | 'ocr' | 'api';
  metadata?: Record<string, any>;
  originalText?: string;
  isRecurring?: boolean;
  recurringPattern?: string;
  exchangeRate?: number;
  originalAmount?: number;
  originalCurrency?: string;
  /** Who owes what part; left out, the context's own rule applies to expenses. */
  split?: SplitRequest;
}

export interface UpdateTransactionData extends Partial<CreateTransactionData> {
  status?: 'pending' | 'confirmed' | 'cancelled';
}

export interface TransactionFilters {
  startDate?: string;
  endDate?: string;
  type?: 'expense' | 'income' | 'transfer';
  status?: 'pending' | 'confirmed' | 'cancelled';
  category?: string;
  subcategory?: string;
  dashboardCategory?: string;
  /** Scope results to a shared context instead of the caller's own records. */
  contextId?: string;
  currency?: string;
  merchantName?: string;
  search?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  limit?: number;
  sortBy?: 'date' | 'amount' | 'description' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

export interface TransactionSummary {
  totalIncome: number;
  totalExpenses: number;
  netAmount: number;
  transactionCount: number;
  /** Spending by dashboard category; `category` is the key, e.g. "fooddining". */
  categories: Array<{
    category: string;
    amount: number;
    count: number;
    percentage: number;
  }>;
}

export interface PaginatedTransactions {
  transactions: Transaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  summary: TransactionSummary;
}

export interface CalendarDay {
  /** Calendar date, YYYY-MM-DD. */
  date: string;
  income: number;
  expense: number;
  count: number;
}

export const transactionApi = {
  /** Per-day totals for a month — only days with movement come back. */
  /** Income and expense per month for the last N months, zero-filled. */
  async getMonthly(
    months: number,
    contextId?: string,
  ): Promise<Array<{ month: string; income: number; expense: number }>> {
    const params = new URLSearchParams({ months: String(months) });
    if (contextId) params.append('contextId', contextId);
    const response = await api.get(`/transactions/monthly?${params.toString()}`);
    return response.data;
  },

  /** Expenses per member of a shared context in the date range. */
  async getByMember(
    contextId: string,
    startDate: string,
    endDate: string,
  ): Promise<Array<{ userId: string; firstName: string; lastName: string; expense: number }>> {
    const params = new URLSearchParams({ contextId, startDate, endDate });
    const response = await api.get(`/transactions/by-member?${params.toString()}`);
    return response.data;
  },

  async getCalendar(month: string, contextId?: string): Promise<CalendarDay[]> {
    const response = await api.get('/transactions/calendar', {
      params: { month, ...(contextId ? { contextId } : {}) },
    });
    return response.data;
  },

  async getTransactions(filters?: TransactionFilters): Promise<PaginatedTransactions> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(`/transactions?${params.toString()}`);
    return response.data;
  },

  async getTransaction(id: string): Promise<Transaction> {
    const response = await api.get(`/transactions/${id}`);
    return response.data;
  },

  async createTransaction(data: CreateTransactionData): Promise<Transaction> {
    // The API reads the target context from the query string, not the body.
    const { contextId, ...body } = data;
    const response = await api.post('/transactions', body, {
      params: contextId ? { contextId } : undefined,
    });
    return response.data;
  },

  async updateTransaction(id: string, data: UpdateTransactionData): Promise<Transaction> {
    const response = await api.patch(`/transactions/${id}`, data);
    return response.data;
  },

  async deleteTransaction(id: string): Promise<void> {
    await api.delete(`/transactions/${id}`);
  },

  async confirmTransaction(id: string): Promise<Transaction> {
    const response = await api.post(`/transactions/${id}/confirm`);
    return response.data;
  },

  async cancelTransaction(id: string): Promise<Transaction> {
    const response = await api.post(`/transactions/${id}/cancel`);
    return response.data;
  },

  async getCategories(): Promise<string[]> {
    const response = await api.get('/transactions/categories');
    return response.data;
  },

  async getMerchants(): Promise<string[]> {
    const response = await api.get('/transactions/merchants');
    return response.data;
  },
};